import React, { useEffect, useRef, useState } from 'react';
import { app } from '../../firebase.js';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import html2pdf from 'html2pdf.js';
import { BRAND_LOGO_ALT, ORGANIZATION_NAME, brandLogo } from '../../branding';
import './financeDocuments.css';

const formatDate = (value) => {
  if (!value) return '--';
  const rawDate = typeof value.toDate === 'function' ? value.toDate() : new Date(value);

  return new Intl.DateTimeFormat('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(rawDate);
};

const formatCurrencyAmount = (value, currency = 'KES') => {
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';
  const numericAmount = Number(value || 0);

  if (!Number.isFinite(numericAmount)) {
    return `${safeCurrency} 0`;
  }

  return `${safeCurrency} ${numericAmount.toLocaleString(safeCurrency === 'USD' ? 'en-US' : 'en-KE', {
    minimumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
    maximumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
  })}`;
};

const resolveInvoiceDisplay = (invoiceData = {}) => {
  const currency = invoiceData.displayCurrency || invoiceData.paymentCurrency || invoiceData.currency || 'KES';
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';

  if (safeCurrency === 'USD') {
    return {
      amount: Number(invoiceData.displayAmount || invoiceData.amount || 0),
      currency: safeCurrency,
    };
  }

  return {
    amount: Number(invoiceData.amountInKes || invoiceData.amount || 0),
    currency: safeCurrency,
  };
};

function ViewInvoice() {
  const [invoiceData, setInvoiceData] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [studentProfile, setStudentProfile] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [qrUnavailable, setQrUnavailable] = useState(false);
  const invoiceRef = useRef(null);

  const receivedId = decodeURIComponent(window.location.search);
  const docID = parseInt(receivedId.substring(1), 10);
  const db = getFirestore(app);
  const invoiceUrl = window.location.href;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=0&data=${encodeURIComponent(invoiceUrl)}`;

  useEffect(() => {
    const fetchInvoice = async () => {
      setIsLoading(true);
      setLoadError('');
      setQrUnavailable(false);

      if (Number.isNaN(docID)) {
        setLoadError('The invoice link is invalid.');
        setIsLoading(false);
        return;
      }

      try {
        const invoiceQuery = query(collection(db, 'Invoices'), where('invoiceNumber', '==', docID));
        const invoiceSnapshot = await getDocs(invoiceQuery);

        if (invoiceSnapshot.empty) {
          setLoadError('Invoice not found.');
          return;
        }

        const data = invoiceSnapshot.docs[0].data();
        setInvoiceData(data);

        const userQuery = query(collection(db, 'users'), where('userId', '==', data.studentId));
        const userSnapshot = await getDocs(userQuery);

        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          setStudentName(userData.bio || data.studentName || '');
          setAdmissionNumber(userData.admissionNumber || data.prospectAdmissionNumber || '');
          setStudentProfile(userData);
          return;
        }

        setStudentName(data.studentName || '');
        setAdmissionNumber(data.prospectAdmissionNumber || '');
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setLoadError('We could not load this invoice right now.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [db, docID]);

  const exportPDF = () => {
    if (!invoiceRef.current) return;

    html2pdf()
      .from(invoiceRef.current)
      .set({
        margin: 10,
        filename: `${docID}_invoice.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .save();
  };

  if (isLoading) {
    return (
      <div className="financeDocumentPage">
        <div className="financeDocumentStateCard">
          <p className="financeDocumentStateEyebrow">Fee invoice</p>
          <h2>Loading invoice...</h2>
          <p className="mb-0">Please wait while we prepare the invoice details.</p>
        </div>
      </div>
    );
  }

  if (loadError || !invoiceData) {
    return (
      <div className="financeDocumentPage">
        <div className="financeDocumentStateCard">
          <p className="financeDocumentStateEyebrow">Fee invoice</p>
          <h2>Invoice unavailable</h2>
          <p className="mb-0">{loadError || 'We could not find the requested invoice.'}</p>
        </div>
      </div>
    );
  }

  const invoiceDisplay = resolveInvoiceDisplay(invoiceData);
  const formattedInvoiceAmount = formatCurrencyAmount(invoiceDisplay.amount, invoiceDisplay.currency);
  const courseLabel = invoiceData.course || studentProfile.course || invoiceData.studentCourse || 'Fee invoice';
  const paymentRoute = invoiceData.paymentInstructionType === 'student_account_only'
    ? 'Pay from the student account'
    : 'Pay from the student account or college paybill';

  return (
    <div className="financeDocumentPage">
      <div className="financeDocumentShell">
        <div ref={invoiceRef} className="financeDocumentCard">
          <div className="financeDocumentHeader">
            <div className="financeDocumentBrand">
              <img src={brandLogo} className="financeDocumentBrandLogo" alt={BRAND_LOGO_ALT} />
              <div>
                <p className="financeDocumentEyebrow">{ORGANIZATION_NAME}</p>
                <h1>Fee Invoice</h1>
                <p className="financeDocumentMuted mb-0">Official student fee invoice</p>
              </div>
            </div>
            <div className="financeDocumentHeaderMeta">
              <div className="financeDocumentMetaStack">
                <span className="financeDocumentMetaLabel">Invoice No.</span>
                <strong>{invoiceData.invoiceNumber}</strong>
              </div>
              <div className="financeDocumentMetaStack">
                <span className="financeDocumentMetaLabel">Issue date</span>
                <strong>{formatDate(invoiceData.timeCreated)}</strong>
              </div>
            </div>
          </div>

          <div className="financeDocumentDivider" />

          <div className="financeDocumentTopGrid">
            <div className="financeDocumentPartyBlock">
              <p className="financeDocumentSectionEyebrow">Billed to</p>
              <h2>{studentName || 'Student record unavailable'}</h2>
              <p className="financeDocumentPartyLine">Admission No. {admissionNumber || '--'}</p>
              <div className="financeDocumentMetaList">
                <div className="financeDocumentMetaListRow">
                  <span>Programme</span>
                  <strong>{courseLabel}</strong>
                </div>
                <div className="financeDocumentMetaListRow">
                  <span>Due date</span>
                  <strong>{formatDate(invoiceData.dueDate)}</strong>
                </div>
                <div className="financeDocumentMetaListRow">
                  <span>Payment route</span>
                  <strong>{paymentRoute}</strong>
                </div>
              </div>
            </div>

            <div className="financeDocumentQrPanel">
              <p className="financeDocumentSectionEyebrow">Invoice QR</p>
              <div className="financeDocumentQrFrame">
                {qrUnavailable ? (
                  <div className="financeDocumentQrFallback">
                    <span>Invoice link</span>
                    <strong>{invoiceData.invoiceNumber}</strong>
                  </div>
                ) : (
                  <img
                    src={qrCodeUrl}
                    alt={`QR code for invoice ${invoiceData.invoiceNumber}`}
                    className="financeDocumentQrImage"
                    crossOrigin="anonymous"
                    onError={() => setQrUnavailable(true)}
                  />
                )}
              </div>
              <p className="financeDocumentQrCaption mb-0">Scan to open this invoice online.</p>
            </div>
          </div>

          <div className="financeDocumentSnapshotGrid">
            <div className="financeDocumentSnapshotItem">
              <span className="financeDocumentMetaLabel">Amount due</span>
              <strong>{formattedInvoiceAmount}</strong>
            </div>
            <div className="financeDocumentSnapshotItem">
              <span className="financeDocumentMetaLabel">Due date</span>
              <strong>{formatDate(invoiceData.dueDate)}</strong>
            </div>
            <div className="financeDocumentSnapshotItem">
              <span className="financeDocumentMetaLabel">Reference</span>
              <strong>{invoiceData.invoiceNumber}</strong>
            </div>
          </div>

          <div className="financeDocumentSupportGrid">
            <div className="financeDocumentSupportCard">
              <p className="financeDocumentSectionEyebrow">Payment instructions</p>
              <div className="financeDocumentMetaList financeDocumentMetaList--compact">
                <div className="financeDocumentMetaListRow">
                  <span>Student account</span>
                  <strong>Use the student fee invoices page to complete this payment.</strong>
                </div>
                {invoiceData.paybill ? (
                  <div className="financeDocumentMetaListRow">
                    <span>Paybill</span>
                    <strong>{invoiceData.paybill}</strong>
                  </div>
                ) : null}
                {invoiceData.accountNumber ? (
                  <div className="financeDocumentMetaListRow">
                    <span>Account number</span>
                    <strong>{invoiceData.accountNumber}</strong>
                  </div>
                ) : null}
                {invoiceDisplay.currency === 'USD' && invoiceData.exchangeRateUsdToKes ? (
                  <div className="financeDocumentMetaListRow">
                    <span>Exchange rate</span>
                    <strong>1 USD = KES {Number(invoiceData.exchangeRateUsdToKes).toLocaleString('en-KE')}</strong>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="financeDocumentSupportCard">
              <p className="financeDocumentSectionEyebrow">Notes</p>
              <p className="financeDocumentSupportCopy">
                Keep this invoice reference for payment follow-up and future reconciliation. Once payment is received, the matching receipt will be available through the receipt view link.
              </p>
            </div>
          </div>

          <div className="financeDocumentTableWrap">
            <table className="table financeDocumentTable mb-0">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Due Date</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Description">{courseLabel}</td>
                  <td data-label="Due Date">{formatDate(invoiceData.dueDate)}</td>
                  <td data-label="Amount" className="text-end">{formattedInvoiceAmount}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="financeDocumentTotalBar">
            <span>Total due</span>
            <strong>{formattedInvoiceAmount}</strong>
          </div>

          <div className="financeDocumentFooter">
            <div className="financeDocumentFooterMeta">
              <span>This invoice is computer-generated and does not require a signature.</span>
              <strong>Reference: {invoiceData.invoiceNumber}</strong>
            </div>
            <p className="mb-0">Use the student portal to complete payment before the due date shown above.</p>
          </div>
        </div>

        <div className="financeDocumentActions">
          <button onClick={exportPDF} className="financeDocumentDownloadBtn">
            Download as PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewInvoice;
